import { createContext, useContext } from 'react';
import type { ColumnDragItem, OETDragItem, RowDragItem } from './DnDTypes';

/**
 * The OET drag state shared with every drop target so it can re-derive its own
 * `isOver && canDrop` the same way the react-dnd monitor did. `activeItem` is
 * the item produced by the current drag source (`null` when nothing is being
 * dragged); `overId` is the droppable id the pointer is currently over (the
 * dnd-kit `over` collision result), or `null`.
 */
export interface OETDragState {
  activeItem: OETDragItem | null;
  overId: string | null;
}

const OETDragContext = createContext<OETDragState>({ activeItem: null, overId: null });

export default OETDragContext;

/** Read the current OET drag state. */
export function useOETDragState(): OETDragState {
  return useContext(OETDragContext);
}

// --- canDrop predicates (single source of truth, ported 1:1 from the react-dnd
//     `canDrop` specs so the reorder semantics stay byte-identical). ---

/**
 * Can the dragged column be dropped onto the header cell at `columnIndex`?
 * (`OETHeaderCell` `columnTarget.canDrop`.) The dragged column moves to *after*
 * the cell it is dropped on, so dropping on itself or on the cell directly in
 * front of it is a no-op.
 */
export function canDropColumnOnCell(item: ColumnDragItem, columnIndex: number): boolean {
  return columnIndex !== item.index && columnIndex !== item.index - 1;
}

/**
 * Can the dragged column be dropped onto the corner / first cell?
 * (`OETFirstHeaderCell` `columnTarget.canDrop`.) Dropping the first column to
 * the start is a no-op.
 */
export function canDropColumnOnFirst(item: ColumnDragItem): boolean {
  return item.index !== 0;
}

/**
 * Can the dragged row be dropped onto the row whose event id is `rowEventId`,
 * dnd group `rowGroup` and index `rowIndex`? (`OETRow` `rowTarget.canDrop`.)
 *
 * Two ways a drop is valid:
 *  - onto the parent row (`rowEventId === item.group`) to move to the top of the
 *    sub-rows — but dropping the first sub-row there is a no-op (`item.index !== 0`);
 *  - onto a sibling in the same group (`rowGroup === item.group`); the dragged row
 *    moves to *below* the target, so dropping on itself or the row above is a no-op.
 */
export function canDropRowOnRow(
  item: RowDragItem,
  rowEventId: string,
  rowGroup: string,
  rowIndex: number
): boolean {
  return (
    (rowEventId === item.group && item.index !== 0) ||
    (rowGroup === item.group && rowIndex !== item.index && rowIndex !== item.index - 1)
  );
}

/**
 * Can the dragged row be dropped onto the header (`<thead>`, drop-before-first)?
 * (`OETHeader` `rowTarget.canDrop`.) Only top-level rows, and dropping the first
 * row there is a no-op.
 */
export function canDropRowOnHeader(item: RowDragItem): boolean {
  return item.group === 'top' && item.index !== 0;
}
